/**
 * Email Service Module
 * 
 * Handles sending emails to patients and practice-specific email addresses
 * after form submission using Mailgun via backend API.
 */

// Configuration for API endpoint
const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || 
  (typeof window !== 'undefined' && process.env.NODE_ENV === 'production' 
    ? `${window.location.origin}/api` 
    : 'http://localhost:3001/api');

/**
 * Send form submission confirmation emails via backend API
 * @param {Object} formData - The complete form data
 * @returns {Promise} - Promise resolving to the email sending result
 */
export const sendFormEmails = async (formData) => {
  try {
    // Add detailed logging to diagnose environment and API selection
    console.log('============== EMAIL SERVICE DIAGNOSTICS ==============');
    console.log('NODE_ENV:', process.env.NODE_ENV);
    console.log('REACT_APP_USE_REAL_API:', process.env.REACT_APP_USE_REAL_API);
    console.log('Window location origin:', typeof window !== 'undefined' ? window.location.origin : 'N/A');
    console.log('Using development mode?', process.env.NODE_ENV === 'development');
    console.log('Using mock API?', process.env.NODE_ENV === 'development' && !process.env.REACT_APP_USE_REAL_API);
    console.log('====================================================');

    // Determine practice-specific email based on selected practice
    const practiceEmail = getPracticeEmail(formData.practice);
    
    // Prepare email data
    const emailData = {
      patientEmail: formData.email,
      practiceEmail: practiceEmail,
      subject: `Patient Visit Questionnaire Confirmation - ${formData.name}`,
      patientName: formData.name,
      formData: formData,
      submissionDate: new Date().toISOString()
    };
    
    // For development without backend running, use mockFetch from mockEmailApi
    // In production, this should be replaced with a real API call to your backend
    let apiUrl;
    if (process.env.NODE_ENV === 'development' && !process.env.REACT_APP_USE_REAL_API) {
      console.log('Using mock email API for development');
      // Import the mockFetch function dynamically
      const { mockFetch } = await import('./mockEmailApi');
      return mockFetch('/api/send-emails', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emailData),
      }).then(response => response.json());
    }
    
    // Real API call to backend
// Use current window origin for production, fallback to localhost for development
const baseUrl = (process.env.NODE_ENV === 'production' && typeof window !== 'undefined')
  ? window.location.origin
  : 'http://localhost:3001';
  
apiUrl = `${baseUrl}/api/send-emails`;
console.log(`Sending emails via API: ${apiUrl}`);

const response = await fetch(apiUrl, {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
  },
  body: JSON.stringify(emailData),
});
    
    if (!response || !response.ok) {
      // Handle case where response is not a valid Response object
      if (response && typeof response.text === 'function') {
        try {
          const errorText = await response.text();
          throw new Error(`Failed to send emails: ${response.statusText}. ${errorText}`);
        } catch (textError) {
          throw new Error(`Failed to send emails: ${response ? response.statusText : 'No response'}`); 
        }
      } else {
        throw new Error(`Failed to connect to email API server at ${apiUrl}. Is the server running?`);
      }
    }
    
    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error sending emails:', error);
    throw new Error(`Failed to send confirmation emails: ${error.message}`);
  }
};

/**
 * Get practice-specific email based on practice name
 * @param {string} practiceName - Name of the selected practice
 * @returns {string} - Practice email address
 */
export const getPracticeEmail = (practiceName) => {
  // First check for environment variables (more secure)
  if (practiceName === 'STARS Plastic Surgery' && process.env.REACT_APP_EMAIL_STARS_PLASTIC_SURGERY) {
    return process.env.REACT_APP_EMAIL_STARS_PLASTIC_SURGERY;
  }
  if (practiceName === 'San Antonio General Surgery' && process.env.REACT_APP_EMAIL_SAN_ANTONIO_GENERAL_SURGERY) {
    return process.env.REACT_APP_EMAIL_SAN_ANTONIO_GENERAL_SURGERY;
  }
  
  // Fallback to hardcoded values if environment variables are not set
  // NOTE: These should be updated to working email addresses that exist and can receive email
  const practiceEmails = {
    'STARS Plastic Surgery': 'rodneykchan@gmail.com', // Temporary testing address
    'San Antonio General Surgery': 'rodneykchan@gmail.com', // Temporary testing address
    // Add more practices as needed
  };
  
  return practiceEmails[practiceName] || 'default@yourpractice.com';
};

/**
 * Generate HTML content for patient email
 * @param {Object} formData - The form data
 * @returns {string} - HTML content for email
 */
export const generatePatientEmailContent = (formData) => {
  // Generate a formatted HTML email with form data for patient
  // This is a simplified example - you would want to make this more comprehensive
  return `
    <html>
      <head>
        <style>
          body { font-family: Arial, sans-serif; line-height: 1.6; }
          .container { max-width: 600px; margin: 0 auto; padding: 20px; }
          .header { background: #f8f9fa; padding: 15px; text-align: center; }
          .content { padding: 20px 0; }
          .footer { font-size: 12px; text-align: center; margin-top: 30px; color: #6c757d; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #dee2e6; padding: 8px; text-align: left; }
          th { background-color: #f8f9fa; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h2>Patient Visit Questionnaire Confirmation</h2>
            <p>Thank you for completing your questionnaire with ${formData.practice}</p>
          </div>
          <div class="content">
            <p>Dear ${formData.name},</p>
            <p>Thank you for completing your patient questionnaire. This email serves as confirmation of your submission.</p>
            <p>Below is a summary of the information you provided:</p>
            
            <h3>Patient Information</h3>
            <table>
              <tr><th>Name</th><td>${formData.name}</td></tr>
              <tr><th>Date of Birth</th><td>${formData.date_of_birth}</td></tr>
              <tr><th>Gender</th><td>${formData.gender}</td></tr>
              <tr><th>Email</th><td>${formData.email}</td></tr>
              <tr><th>Phone</th><td>${formData.cell_phone}</td></tr>
              <tr><th>Preferred Pharmacy</th><td>${formData.preferred_pharmacy}</td></tr>
            </table>
            
            <p>Please keep this email for your records. If you need to make any changes to your information, 
            please contact our office directly.</p>
            
            <p>Best regards,<br>${formData.practice} Team</p>
          </div>
          <div class="footer">
            <p>This is an automated message. Please do not reply to this email.</p>
            <p>&copy; ${new Date().getFullYear()} ${formData.practice}. All rights reserved.</p>
          </div>
        </div>
      </body>
    </html>
  `;
};

/**
 * Generate HTML content for practice email
 * @param {Object} formData - The form data
 * @returns {string} - HTML content for email
 */
export const generatePracticeEmailContent = (formData) => {
  // Generate a comprehensive HTML email with all form data for the practice
  let medicalProblems = formData.medical_problems ? formData.medical_problems.join(', ') : 'None';
  if (formData.medical_problems && formData.medical_problems.includes('Other')) {
    medicalProblems += ` (Other: ${formData.other_medical_problem})`;
  }

  return `
    <html>
      <head>
        <style>
          body { font-family: Arial, sans-serif; line-height: 1.6; }
          .container { max-width: 800px; margin: 0 auto; padding: 20px; }
          .header { background: #f8f9fa; padding: 15px; text-align: center; }
          .content { padding: 20px 0; }
          .section { margin-bottom: 20px; }
          h3 { border-bottom: 1px solid #dee2e6; padding-bottom: 8px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
          th, td { border: 1px solid #dee2e6; padding: 8px; text-align: left; }
          th { background-color: #f8f9fa; width: 30%; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h2>New Patient Questionnaire Submission</h2>
            <p>Submitted on ${new Date().toLocaleString()}</p>
          </div>
          <div class="content">
            <div class="section">
              <h3>Patient Information</h3>
              <table>
                <tr><th>Name</th><td>${formData.name || 'N/A'}</td></tr>
                <tr><th>Date of Birth</th><td>${formData.date_of_birth || 'N/A'}</td></tr>
                <tr><th>Gender</th><td>${formData.gender || 'N/A'}</td></tr>
                <tr><th>Email</th><td>${formData.email || 'N/A'}</td></tr>
                <tr><th>Phone</th><td>${formData.cell_phone || 'N/A'}</td></tr>
                <tr><th>Address</th><td>${[formData.street_address, formData.city, formData.state, formData.zip_code].filter(Boolean).join(', ') || 'N/A'}</td></tr>
                <tr><th>Preferred Pharmacy</th><td>${formData.preferred_pharmacy || 'N/A'}</td></tr>
                <tr><th>How Found</th><td>${formData.how_found_us || 'N/A'}</td></tr>
                <tr><th>Referral Source</th><td>${formData.referral_source || 'N/A'}</td></tr>
              </table>
            </div>
            
            <div class="section">
              <h3>Insurance Information</h3>
              <table>
                <tr><th>Has Insurance</th><td>${formData.has_insurance || 'N/A'}</td></tr>
                ${formData.has_insurance === 'Yes' ? `
                <tr><th>Insurance Provider</th><td>${formData.insurance_provider || 'N/A'}</td></tr>
                <tr><th>Insurance ID</th><td>${formData.insurance_id || 'N/A'}</td></tr>
                <tr><th>Insurance Group</th><td>${formData.insurance_group || 'N/A'}</td></tr>
                <tr><th>Policy Holder</th><td>${formData.policy_holder || 'N/A'}</td></tr>` : ''}
              </table>
            </div>
            
            <div class="section">
              <h3>Medical History</h3>
              <table>
                <tr><th>Medical Problems</th><td>${medicalProblems}</td></tr>
                <tr><th>Taking Medication</th><td>${formData.taking_medication || 'N/A'}</td></tr>
                <tr><th>Medications</th><td>${formData.medications || 'None'}</td></tr>
                <tr><th>Has Allergies</th><td>${formData.has_allergies || 'N/A'}</td></tr>
                <tr><th>Allergies</th><td>${formData.allergies || 'None'}</td></tr>
              </table>
            </div>
            
            <div class="section">
              <h3>Other History</h3>
              <table>
                <tr><th>Surgical History</th><td>${formData.surgical_history || 'None'}</td></tr>
                <tr><th>Family History</th><td>${formData.family_history || 'None'}</td></tr>
                <tr><th>Social History</th><td>${formData.social_history || 'None'}</td></tr>
              </table>
            </div>
            
            <div class="section">
              <h3>Current Concerns</h3>
              <table>
                <tr><th>Chief Concern</th><td>${formData.chief_concern || 'N/A'}</td></tr>
                <tr><th>Concern Duration</th><td>${formData.concern_duration || 'N/A'}</td></tr>
                <tr><th>Additional Details</th><td>${formData.concern_details || 'None'}</td></tr>
              </table>
            </div>
          </div>
        </div>
      </body>
    </html>
  `;
};
